// built-from: /application-navigation-shell v2 impl:application-navigation-shell role:implementation
// EngagementSwitcher — the sidebar chip rebound as the engagement switcher
// (027-engagement-aggregate, Slice C, FR-010, User Story 2). It renders the
// current engagement as a trigger button (aria-haspopup / aria-expanded /
// aria-label + a ChevronsUpDown caret) and, on click, an anchored dropdown that:
//   • lists the engagements — the CURRENT one marked with a Check (text + icon,
//     not colour alone) and aria-checked, the others as menuitems;
//   • ends with a "New engagement" footer row (Plus) that opens the create modal;
//   • supports ArrowDown/ArrowUp roving + Enter to select, Escape to close,
//     click-outside (mousedown) to dismiss; focus returns to the trigger on close;
//   • selecting another engagement calls switchEngagement(id) and fires a brief
//     "Now in: {Client · Programme}" role="status" confirmation (non-blocking).
//
// CORAL DISCIPLINE: the "New engagement" footer and the create CTA are neutral —
// coral (--ap-accent) is reserved for the first-run gateway alone.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEngagement } from '../hooks/useEngagement';
import type { EngagementSummary } from '../hooks/useEngagement';
import CreateEngagementModal from './CreateEngagementModal';
import '../styles/EngagementSwitcher.css';

function avatarOf(client: string): string {
  return client.trim().charAt(0).toUpperCase() || '·';
}

function nowInLabel(client: string, programme: string): string {
  return `Now in: ${client} · ${programme}`;
}

function engagementName(summary: Pick<EngagementSummary, 'programme' | 'lifecycle'>): string {
  return summary.lifecycle === 'draft' ? `${summary.programme} · Draft` : summary.programme;
}

function searchable(value: string): string {
  return value.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

const EngagementSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const {
    engagements,
    currentEngagement,
    currentEngagementId,
    switchEngagement,
    createEngagement,
  } = useEngagement();

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // A non-blocking "Now in…" confirmation (role="status"). It is replaced on the
  // next switch/create rather than auto-expiring on a timer — a persistent inline
  // confirmation, mirroring OrganisationContext's "Saved" status.
  const announce = useCallback((client: string, programme: string) => {
    setConfirmation(nowInLabel(client, programme));
  }, []);

  const closeMenu = useCallback((restoreFocus = true) => {
    setOpen(false);
    setQuery('');
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  // Click-outside (mousedown) dismisses the menu.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // When the menu opens, seat the roving cursor on the current engagement.
  const visibleEngagements = useMemo(() => engagements.filter((engagement) => {
    const haystack = [engagement.client, engagement.title, engagement.programme].filter(Boolean).join(' ');
    return searchable(haystack).includes(searchable(query));
  }), [engagements, query]);

  useEffect(() => {
    if (!open) return;
    const idx = visibleEngagements.findIndex((e) => e.id === currentEngagementId);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [open, currentEngagementId, visibleEngagements]);

  const handleSelect = useCallback(
    async (eng: EngagementSummary) => {
      closeMenu();
      let selected = true;
      if (eng.id !== currentEngagementId) {
        selected = await switchEngagement(eng.id);
      }
      if (!selected) {
        setConfirmation(`Could not open ${eng.client} · ${eng.programme}`);
        return;
      }
      announce(eng.client, eng.programme);
      navigate('/engagement');
    },
    [closeMenu, currentEngagementId, switchEngagement, announce, navigate],
  );

  const handleCreate = useCallback(
    async (input: { client: string; title: string; commandId: string }) => {
      // 075: new engagements are brief-first — live from creation, landing on
      // the Brief so the plan builds from the narration.
      const created = await createEngagement({ ...input, flow: 'brief' });
      if (created) {
        announce(created.client, created.programme);
        navigate('/engagement/brief');
      }
      return created;
    },
    [createEngagement, announce, navigate],
  );

  const openCreate = useCallback(() => {
    setOpen(false);
    setQuery('');
    setModalOpen(true);
  }, []);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    // The footer "New engagement" row is the last roving stop.
    const lastIndex = visibleEngagements.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(lastIndex, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= visibleEngagements.length) {
        openCreate();
      } else {
        const eng = visibleEngagements[activeIndex];
        if (eng) handleSelect(eng);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
    }
  };

  const client = currentEngagement?.client ?? null;
  const triggerLabel = client
    ? `Switch engagement — current: ${client}`
    : 'Switch engagement';

  return (
    <div className="ap-shell-org eng-switcher">
      <button
        ref={triggerRef}
        type="button"
        className="ap-shell-org-row eng-switcher-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerLabel}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="ap-shell-org-avatar" aria-hidden="true">
          {client ? avatarOf(client) : '·'}
        </span>
        <span className="ap-shell-org-meta">
          {currentEngagement && client ? (
            <>
              <span className="ap-shell-org-name">{client}</span>
              <span className="ap-shell-org-sub">{engagementName(currentEngagement)}</span>
            </>
          ) : (
            <span className="ap-shell-org-name ap-shell-org-name--placeholder">
              No engagement set
            </span>
          )}
        </span>
        <ChevronsUpDown size={16} className="ap-shell-org-caret" aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={menuRef}
          className="eng-switcher-menu"
          role="menu"
          aria-label="Engagements"
          tabIndex={-1}
          onKeyDown={onMenuKeyDown}
        >
          <label className="eng-switcher-search">
            <Search size={15} aria-hidden="true" />
            <span className="eng-switcher-sr">Find engagement</span>
            <input
              type="search"
              placeholder="Find engagement"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') closeMenu();
                else if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) event.stopPropagation();
              }}
            />
          </label>
          {visibleEngagements.length === 0 ? (
            <p className="eng-switcher-empty">No matching engagement.</p>
          ) : null}
          {visibleEngagements.map((eng, i) => {
            const isCurrent = eng.id === currentEngagementId;
            return (
              <button
                key={eng.id}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                className={`eng-switcher-item${isCurrent ? ' eng-switcher-item--current' : ''}${
                  i === activeIndex ? ' eng-switcher-item--active' : ''
                }`}
                onClick={() => handleSelect(eng)}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <span className="eng-switcher-item-avatar" aria-hidden="true">
                  {avatarOf(eng.client)}
                </span>
                <span className="eng-switcher-item-meta">
                  <span className="eng-switcher-item-name">{eng.client}</span>
                  <span className="eng-switcher-item-sub">{engagementName(eng)}</span>
                </span>
                {isCurrent && (
                  <span className="eng-switcher-item-check">
                    <Check size={16} aria-hidden="true" />
                    <span className="eng-switcher-sr">Current</span>
                  </span>
                )}
              </button>
            );
          })}

          <button
            type="button"
            role="menuitem"
            className={`eng-switcher-new${
              activeIndex >= visibleEngagements.length ? ' eng-switcher-item--active' : ''
            }`}
            onClick={openCreate}
            onMouseEnter={() => setActiveIndex(visibleEngagements.length)}
          >
            <span className="eng-switcher-new-icon" aria-hidden="true">
              <Plus size={16} />
            </span>
            New engagement
          </button>
        </div>
      )}

      <CreateEngagementModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreate}
      />

      {/* Non-blocking "Now in…" confirmation (role="status", not a dialog). */}
      <div className="eng-switcher-status" role="status" aria-live="polite">
        {confirmation}
      </div>
    </div>
  );
};

export default EngagementSwitcher;
